import { trackError } from '@proposify/frontend-observability';
import type { TableRowType } from 'components/editor/grid/reduxStore/table.types';

export type CrmFieldInstanceList = NonNullable<TableRowType['crmFieldInstances']>[string];

// crmFieldInstances is not covered by assertCrmRowMarkersValid / crmRowMarkerFieldsSchema,
// so a malformed (non-array) value can survive save and reload untouched. Every reader of
// crmFieldInstances must tolerate that instead of trusting the TableRowType shape.
export const isCrmFieldInstanceList = (value: unknown): value is CrmFieldInstanceList => Array.isArray(value);

// Session-scoped (module-lifetime) dedupe: one report per malformed field+row+key per browser
// session, not per component and not per paste. Repeated pastes into the same broken row
// are common (user retries) and would otherwise re-report on every attempt. Test support:
// resetCrmFieldInstancesReporting() below.
const reportedKeys = new Set<string>();

// Shared by crmFieldInstances (below) and crmFieldGroups (see deriveCrmRows.ts) — both are
// row-persisted, untyped-storage maps that can carry a malformed (non-array) value past save
// and reload, and both need the same "report once, then read as absent" handling.
export function reportMalformedCrmField(fieldName: string, rowId: string | undefined, key: string, value: unknown): void {
  const dedupeKey = `${fieldName}::${rowId ?? 'unknown-row'}::${key}`;
  if (reportedKeys.has(dedupeKey)) return;
  reportedKeys.add(dedupeKey);

  trackError(new Error(`Malformed ${fieldName} value on row ${rowId ?? 'unknown'} under key "${key}" (typeof ${typeof value})`), {
    errorFrom: 'editor-remote',
    feature: 'crm-table-rows',
  });
}

export function reportMalformedCrmFieldInstances(rowId: string | undefined, key: string, value: unknown): void {
  reportMalformedCrmField('crmFieldInstances', rowId, key, value);
}

// Every entry is visited (filter, not some/find) so reporting is order-independent: a
// malformed entry is skipped, exactly matching today's behavior for a row with no entry
// under that key.
export function validCrmFieldInstanceEntries(row: TableRowType | undefined): [string, CrmFieldInstanceList][] {
  return Object.entries(row?.crmFieldInstances ?? {}).filter((entry): entry is [string, CrmFieldInstanceList] => {
    const [key, value] = entry;
    if (isCrmFieldInstanceList(value)) return true;
    reportMalformedCrmFieldInstances(row?.id, key, value);
    return false;
  });
}

// Reads a single crmFieldInstances key, guarding the same way: a present-but-malformed
// value reports and reads as absent; a genuinely absent key reports nothing.
export function readCrmFieldInstances(row: TableRowType | undefined, key: string): CrmFieldInstanceList | undefined {
  const value = row?.crmFieldInstances?.[key];
  if (value === undefined) return undefined;
  if (isCrmFieldInstanceList(value)) return value;
  reportMalformedCrmFieldInstances(row?.id, key, value);
  return undefined;
}

// Test support only — not called from production code. clearMocks resets trackError call
// counts between tests but cannot reset this module's Set, so without this the "reports
// once" assertions become order-dependent on other tests in the file.
export function resetCrmFieldInstancesReporting(): void {
  reportedKeys.clear();
}
